<template>
  <div
    v-if="open"
    class="app-detail-host"
    :class="['app-detail-host--' + mode, { 'app-detail-host--wide': wide, 'app-detail-host--medium': medium && !wide }]"
  >
    <div v-if="mode === 'overlay'" class="app-detail-scrim" aria-hidden="true" @click="requestClose('scrim')"></div>
    <aside
      ref="pane"
      class="app-detail"
      :class="['app-detail--' + mode, { 'app-detail--dirty': dirty }]"
      :role="mode === 'side' ? null : 'dialog'"
      :aria-modal="mode === 'side' ? null : 'true'"
      :aria-labelledby="titleId"
      tabindex="-1"
      @keydown.esc="onEsc"
    >
      <header v-if="mode === 'full'" class="app-detail__phonehead">
        <app-icon-button ref="close" icon="arrowLeft" label="Zurück" :tooltip="false" @click="requestClose('back')" />
        <h2 :id="titleId" class="app-detail__phonetitle">{{ title }}</h2>
        <span class="app-detail__subtitle"><slot name="subtitle">{{ subtitle }}</slot></span>
      </header>
      <header v-else class="app-detail__strip">
        <span v-if="icon" class="app-icon-tile" aria-hidden="true"><app-icon :name="icon" :size="14" /></span>
        <h2 :id="titleId" class="app-detail__title">{{ title }}</h2>
        <span class="app-detail__subtitle"><slot name="subtitle">{{ subtitle }}</slot></span>
        <app-icon-button
          ref="close"
          class="app-detail__close"
          icon="x"
          :label="closeLabel"
          :boxed="false"
          :data-flush="touch ? '' : null"
          @click="requestClose('button')"
        />
      </header>
      <div class="app-detail__body"><slot /></div>
      <footer v-if="hasSlot('footer')" class="app-detail__foot"><slot name="footer" /></footer>
    </aside>
  </div>
</template>

<script>
// Detailbereich (Spezifikation 2.10). Modus nach Breite: side (≥ 1264, neben dem Inhalt im Slot detail von AppPage;
// Maus flex 1 1 340px, Touch fest 361), overlay (600–1263, 400 breit von rechts mit Scrim), full (Handy, Vollbild mit
// Zurück-Knopf). Gerendert nur, wenn open. wide (seit 13.10.2026, Import-Log der Fahrpläne): breiter, neben dem Inhalt
// flex 1 1 520px (Touch fest 560), als Overlay 560. medium (seit 14.10.2026, Shop-Tickets: „die ticket detail ansicht
// rechts darf etwas breiter sein“): neben dem Inhalt mit dem Fenster wachsend 340–480 (28vw, bei 1440 rund 400; Touch
// fest 400), als Overlay 440.
// Ereignisse: close ({ reason: 'button' | 'esc' | 'scrim' | 'back' }) – die Seite schließt (bei dirty ggf. nach
// Rückfrage); layout-change ({ open, mode }) nach jedem Öffnen/Schließen/Moduswechsel im $nextTick (Seiten rufen
// darauf grid.updateDimensions()). Slots: default (Körper, scrollt), footer (rechtsbündig), subtitle.
// z-index sitzt direkt an den festen Elementen (Scrim 7, Overlay 8, Vollbild 9): Vuetify berechnet die Ebene von
// Menüs und Tooltips im Bereich aus dem nächsten Vorfahren mit z-index, sie liegen damit darüber.
import AppIcon from './AppIcon.vue';
import AppIconButton from './AppIconButton.vue';

// Ein offenes Vuetify-Menü oder ein Dialog bekommt Esc zuerst
function overlayActive() {
  return !!document.querySelector('.v-menu__content.menuable__content__active, .v-dialog__content--active');
}

export default {
  name: 'AppDetailPane',
  components: { AppIcon, AppIconButton },
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: '' },
    icon: { type: String, default: null },
    subtitle: { type: String, default: null },
    dirty: { type: Boolean, default: false },
    closeLabel: { type: String, default: 'Details schließen' },
    wide: { type: Boolean, default: false },
    medium: { type: Boolean, default: false }
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    mode() {
      if (this.$dichte && this.$dichte.handy) {
        return 'full';
      }
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : window.innerWidth;
      return width >= 1264 ? 'side' : 'overlay';
    },
    titleId() {
      return 'app-detail-' + this._uid + '-titel';
    },
    modal() {
      return this.open && this.mode !== 'side';
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.onOpened();
      } else {
        this.restoreFocus();
      }
      this.notify();
    },
    mode() {
      if (this.open) {
        this.onOpened();
      }
      this.notify();
    },
    modal: {
      immediate: true,
      handler(isModal) {
        if (isModal) {
          document.addEventListener('keydown', this.onDocKeydown);
        } else {
          document.removeEventListener('keydown', this.onDocKeydown);
        }
      }
    }
  },
  created() {
    this.returnFocus = null;
  },
  mounted() {
    if (this.open) {
      this.onOpened();
      this.notify();
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onDocKeydown);
  },
  methods: {
    hasSlot(name) {
      return !!(this.$slots[name] || this.$scopedSlots[name]);
    },
    notify() {
      this.$nextTick(() => this.$emit('layout-change', { open: this.open, mode: this.mode }));
    },
    // Als Overlay/Vollbild Fokus in den Bereich setzen und beim Schließen zurückgeben; daneben bleibt er im Grid
    onOpened() {
      if (this.mode === 'side') {
        return;
      }
      const active = document.activeElement;
      if (active && active !== document.body && !(this.$refs.pane && this.$refs.pane.contains(active))) {
        this.returnFocus = active;
      }
      this.$nextTick(() => {
        const close = this.$refs.close;
        if (close && close.$el) {
          close.$el.focus();
        } else if (this.$refs.pane) {
          this.$refs.pane.focus();
        }
      });
    },
    restoreFocus() {
      const target = this.returnFocus;
      this.returnFocus = null;
      if (target && document.contains(target) && typeof target.focus === 'function') {
        this.$nextTick(() => target.focus());
      }
    },
    requestClose(reason) {
      this.$emit('close', { reason });
    },
    onEsc(e) {
      if (overlayActive()) {
        return;
      }
      e.stopPropagation();
      this.requestClose('esc');
    },
    onDocKeydown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) {
        return;
      }
      if (!this.modal || overlayActive() || (this.$refs.pane && this.$refs.pane.contains(e.target))) {
        return;
      }
      // Esc in einem Dialog oder Menü (z. B. Suche ⌘K über dem Overlay) schließt nur diese Ebene
      if (e.target && e.target.closest && e.target.closest('.v-dialog__content, .v-menu__content')) {
        return;
      }
      this.requestClose('esc');
    }
  }
};
</script>
